import { SEO } from "@/components/SEO";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "@/lib/router-compat";
import {
  ArrowRight,
  Bot,
  Cloud,
  Globe,
  Layers,
  Rocket,
  ShieldCheck,
  Sparkles,
  Workflow,
} from "lucide-react";

const CANONICAL = "https://cargonova.ai/what-is-cargonova";
const CANONICAL_ZH = "https://cargonova.ai/zh/what-is-cargonova";
const CANONICAL_TH = "https://cargonova.ai/th/what-is-cargonova";

const contentEn = {
  eyebrow: "What is CargoNova",
  title: "What is CargoNova?\nThe AI-Native Freight Forwarding Platform",
  summary:
    "CargoNova, also written as Cargo Nova, is an AI-native freight forwarding software platform from Deep Insights. CargoNova AI combines a cloud-native core with specialized AI agents to run quoting, booking, documentation, customs and settlement in one place.",
  intro:
    "CargoNova AI freight management is not just another TMS. It is a freight forwarder system and platform designed from day one around AI agents and cloud-native architecture, so operators can focus on customers instead of data entry.",
  cta1: "Book a demo",
  cta2: "See AI Agents",

  pillarsTitle: "Built on four pillars",
  pillars: [
    { icon: Bot, title: "AI Agent matrix", body: "Seven specialized AI workers cover rates, quoting, booking, documents, customs, settlement and analytics — orchestrated end-to-end." },
    { icon: Cloud, title: "AI-native + cloud-native", body: "AI is core to the architecture, not bolted on. True cloud-native scaling and zero-ops operations across regions." },
    { icon: Globe, title: "Global-ready", body: "Multi-language, multi-currency and multi-regulation from day one. Optimized for Southeast Asia trade lanes and China outbound." },
    { icon: Workflow, title: "Seamless integration", body: "A See–Calculate–Write closed loop: AI agents read data, compute the result and write it back, bridging modern APIs and legacy portals — no rip-and-replace." },
  ],

  audienceTitle: "Who is CargoNova for?",
  audience: [
    "Cross-border e-commerce logistics teams looking for a lighter, smarter forwarding backbone.",
    "SME freight forwarders (10–100 people) growing fast and moving off spreadsheets and email.",
    "Global forwarding groups standardizing operations across branches with multi-language, multi-currency workflows.",
  ],

  valueTitle: "How CargoNova works end-to-end",
  values: [
    { icon: Sparkles, metric: "01", title: "Inquiry & rating", body: "AI freight quotation: agents parse customer inquiries, compare live rates across carriers and return quotes in minutes." },
    { icon: Workflow, metric: "02", title: "Booking & execution", body: "Automated booking with carriers and partners; milestone tracking updates every stakeholder in real time." },
    { icon: Layers, metric: "03", title: "Documents & customs", body: "AI extracts and validates HBL, MBL, invoices and packing lists; customs filings drafted and pre-checked." },
    { icon: Rocket, metric: "04", title: "Settlement & insights", body: "Cost, margin and cash flow reconciled automatically; dashboards surface exceptions and opportunities." },
  ],

  ctaTitle: "See CargoNova in action",
  ctaBody: "Book a personalized walkthrough with our team — or explore how our AI agents map to your workflows.",
  compareLabel: "CargoNova vs CargoWise",
  faqLabel: "See all FAQs",
};

const contentZh: typeof contentEn = {
  eyebrow: "什么是 CargoNova",
  title: "什么是 CargoNova？\nAI Native 国际货代平台解析",
  summary:
    "CargoNova（英文亦写作 Cargo Nova，完整品牌为 CargoNova AI）是 Deep Insights 打造的 AI Native 国际货代系统，在云原生架构之上内置 7 位专职 AI Agent，覆盖报价、订舱、单证、关务与结算的端到端闭环。",
  intro:
    "CargoNova AI 货代系统不是又一个 TMS，而是从第一天起就围绕 AI Agent 与云原生架构设计的货代系统与货代平台，让运营从重复劳动中解放出来，专注客户与决策。",
  cta1: "预约演示",
  cta2: "查看 AI Agent",

  pillarsTitle: "四大核心支柱",
  pillars: [
    { icon: Bot, title: "AI Agent 矩阵", body: "7 位 AI Agent 分别负责运价、报价、订舱、单证、关务、结算与分析，串联端到端业务链路。" },
    { icon: Cloud, title: "AI 原生 + 云原生", body: "AI 从架构底层设计，非后期改造；真正的云原生带来弹性扩展与零运维体验。" },
    { icon: Globe, title: "全球化就绪", body: "内置多语言、多币种、多法规支持，专为东南亚航线与中国出海场景优化。" },
    { icon: Workflow, title: "上下游无缝集成", body: "看-算-写（See–Calculate–Write）自动化闭环：AI Agent 读取数据、自动计算并回写系统，打通现代 API 与老旧系统，无需替换即可落地。" },
  ],

  audienceTitle: "谁在使用 CargoNova？",
  audience: [
    "跨境电商物流团队，希望告别 Excel 与信息孤岛，寻找更轻量、更智能的货代中台。",
    "10–100 人规模、快速成长中的中小型货代企业。",
    "全球化货代集团，需要在多分支间统一流程，实现多语言与多币种协作。",
  ],

  valueTitle: "CargoNova 端到端如何运作",
  values: [
    { icon: Sparkles, metric: "01", title: "询价与报价", body: "智能报价：AI Agent 自动解析客户询盘，实时比价多家船司运价，几分钟内生成精准报价单。" },
    { icon: Workflow, metric: "02", title: "订舱与执行", body: "自动完成船司与合作伙伴订舱，关键节点实时同步，各方随时掌握货物动态。" },
    { icon: Layers, metric: "03", title: "单证与关务", body: "AI 自动提取并校验提单、发票、装箱单等关键单证，提前完成关务申报预审。" },
    { icon: Rocket, metric: "04", title: "结算与洞察", body: "成本、利润与现金流自动对账，仪表盘实时呈现异常与业务机会。" },
  ],

  ctaTitle: "亲身体验 CargoNova",
  ctaBody: "预约团队进行 1:1 演示，或先了解 AI Agent 如何映射到您的业务流程。",
  compareLabel: "CargoNova vs CargoWise",
  faqLabel: "查看常见问题",
};

const contentTh: typeof contentEn = {
  eyebrow: "CargoNova คืออะไร",
  title: "CargoNova คืออะไร?\nแพลตฟอร์ม AI-Native สำหรับฟอร์เวิร์ดเดอร์",
  summary:
    "CargoNova (เขียนได้อีกแบบว่า Cargo Nova) หรือ CargoNova AI คือแพลตฟอร์มซอฟต์แวร์ฟอร์เวิร์ดเดอร์แบบ AI-Native จาก Deep Insights ที่ผสานสถาปัตยกรรมคลาวด์กับ AI Agent เฉพาะทางเพื่อดูแลการเสนอราคา จองระวาง เอกสาร ศุลกากรและการชำระเงินในที่เดียว",
  intro:
    "CargoNova AI ไม่ใช่ TMS ทั่วไป แต่เป็นระบบและแพลตฟอร์ม freight forwarder ที่ออกแบบตั้งแต่วันแรกโดยยึด AI Agent และสถาปัตยกรรมคลาวด์เป็นแกนกลาง เพื่อให้ทีมงานโฟกัสที่ลูกค้าแทนงานซ้ำๆ",
  cta1: "จองเดโม",
  cta2: "ดู AI Agent",

  pillarsTitle: "สี่เสาหลักของแพลตฟอร์ม",
  pillars: [
    { icon: Bot, title: "AI Agent Matrix", body: "AI Agent เฉพาะทาง 7 ตัวครอบคลุมเรท ราคา จอง เอกสาร ศุลกากร การชำระเงินและการวิเคราะห์" },
    { icon: Cloud, title: "AI-Native + Cloud-Native", body: "AI ถูกออกแบบตั้งแต่ระดับสถาปัตยกรรม พร้อมการขยายตัวและ zero-ops แบบคลาวด์แท้จริง" },
    { icon: Globe, title: "พร้อมใช้งานทั่วโลก", body: "รองรับหลายภาษา หลายสกุลเงิน และหลายกฎระเบียบ เหมาะกับเส้นทางเอเชียตะวันออกเฉียงใต้และจีน" },
    { icon: Workflow, title: "เชื่อมต่ออย่างไร้รอยต่อ", body: "วงจรอัตโนมัติแบบ ดู-คำนวณ-เขียน (See–Calculate–Write) โดย AI Agent อ่านข้อมูล คำนวณ และเขียนกลับเข้าระบบ เชื่อม API สมัยใหม่กับระบบเดิมโดยไม่ต้องรื้อระบบ" },
  ],

  audienceTitle: "CargoNova เหมาะกับใคร?",
  audience: [
    "ทีมโลจิสติกส์ข้ามพรมแดนที่ต้องการแบ็คโบนที่เบาและฉลาดกว่าเดิม",
    "ฟอร์เวิร์ดเดอร์ SME ขนาด 10–100 คนที่เติบโตเร็วและกำลังเลิกใช้ Excel และอีเมล",
    "กลุ่มฟอร์เวิร์ดเดอร์ระดับโลกที่ต้องการรวมกระบวนการหลายสาขาในภาษาและสกุลเงินหลากหลาย",
  ],

  valueTitle: "CargoNova ทำงานแบบครบวงจรอย่างไร",
  values: [
    { icon: Sparkles, metric: "01", title: "สอบถามและเสนอราคา", body: "การเสนอราคาอัตโนมัติด้วย AI: Agent วิเคราะห์คำถามลูกค้า เปรียบเทียบเรทจากหลายสายเรือ และส่งใบเสนอราคาภายในไม่กี่นาที" },
    { icon: Workflow, metric: "02", title: "จองและดำเนินการ", body: "จองกับสายเรือและพาร์ทเนอร์อัตโนมัติ อัปเดตสถานะให้ทุกฝ่ายแบบเรียลไทม์" },
    { icon: Layers, metric: "03", title: "เอกสารและศุลกากร", body: "AI สกัดและตรวจสอบ HBL, MBL, ใบแจ้งหนี้และแพ็คกิ้งลิสต์ ยื่นศุลกากรพร้อมพรีเช็ก" },
    { icon: Rocket, metric: "04", title: "ชำระเงินและอินไซต์", body: "ต้นทุน กำไรและกระแสเงินสดกระทบยอดอัตโนมัติ แดชบอร์ดแสดงข้อยกเว้นและโอกาสทันที" },
  ],

  ctaTitle: "สัมผัส CargoNova ด้วยตัวคุณเอง",
  ctaBody: "จองเดโม 1:1 กับทีมของเรา หรือสำรวจว่า AI Agent ของเราตรงกับกระบวนการของคุณอย่างไร",
  compareLabel: "CargoNova vs CargoWise",
  faqLabel: "ดูคำถามที่พบบ่อย",
};

const WhatIsCargoNova = () => {
  const { language } = useLanguage();
  const isZh = language === "zh";
  const isTh = language === "th";
  const c = isZh ? contentZh : isTh ? contentTh : contentEn;
  const prefix = language === "en" ? "" : `/${language}`;

  const seoTitle = isZh
    ? "什么是国际货代 AI 系统（AI FMS）？CargoNova 解析"
    : isTh
    ? "AI FMS คืออะไร? รู้จัก CargoNova แพลตฟอร์ม AI-Native"
    : "What is an AI FMS? CargoNova AI-Native Platform Explained";

  const seoDesc = isZh
    ? "CargoNova 是 Deep Insights 打造的国际货代 AI 系统（AI FMS / AI 原生货代平台），7 位货代 AI 智能体覆盖智能报价、订舱、单证、关务与结算。"
    : isTh
    ? "CargoNova คือ AI FMS (ระบบบริหารงานขนส่งด้วย AI) จาก Deep Insights ซอฟต์แวร์ฟอร์เวิร์ดเดอร์แบบ AI-Native พร้อม AI Agent 7 ตัวครบทุกกระบวนการ"
    : "What is CargoNova: an AI FMS (AI freight management system) by Deep Insights — AI-native freight forwarding software with seven AI agents for quoting, booking, documents, customs and settlement.";

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={seoTitle}
        description={seoDesc}
        canonical={CANONICAL}
        canonicalZh={CANONICAL_ZH}
        canonicalTh={CANONICAL_TH}
      />
      <Navbar />

      <main className="relative pt-24 pb-20 overflow-hidden bg-gradient-to-tr from-secondary/10 via-background to-accent/10">
        <div className="absolute -top-20 -right-20 w-[36rem] h-[36rem] bg-secondary/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -left-32 w-[32rem] h-[32rem] bg-accent/15 rounded-full blur-3xl pointer-events-none" />

        {/* Hero */}
        <section className="relative container mx-auto px-4 max-w-5xl pt-6 md:pt-10 pb-5 md:pb-8">
          <div className="relative">
            <span className="inline-flex items-center gap-1.5 py-1 px-2.5 rounded-full bg-secondary/10 text-secondary text-[11px] font-semibold tracking-wider uppercase mb-4 ring-1 ring-secondary/20">
              <Sparkles className="w-3 h-3" />
              {c.eyebrow}
            </span>
            <h1
              className={`text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mb-3 ${
                isZh ? "leading-[1.5159375]" : "leading-[1.25]"
              } bg-gradient-to-r from-foreground via-secondary to-foreground bg-clip-text text-transparent whitespace-pre-line`}
            >
              {c.title}
            </h1>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-3">
              {c.summary}
            </p>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-6">
              {c.intro}
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://www.deep-insights.ai/book-demo"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-secondary to-accent px-6 py-3 text-sm font-semibold text-secondary-foreground shadow-lg shadow-secondary/30 transition-transform hover:-translate-y-0.5"
              >
                {c.cta1}
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to={`${prefix}/digital-workers`}
                className="inline-flex items-center gap-2 rounded-full ring-1 ring-border/60 bg-card/70 backdrop-blur px-6 py-3 text-sm font-semibold text-foreground hover:ring-secondary/50 hover:text-secondary transition-colors"
              >
                {c.cta2}
              </Link>
            </div>
          </div>
        </section>

        {/* Pillars */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-6">
            {c.pillarsTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {c.pillars.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="group relative overflow-hidden rounded-2xl bg-card ring-1 ring-border/60 shadow-xs hover:shadow-md transition-all duration-300 p-6"
                >
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-secondary to-accent" />
                  <div className="flex items-center gap-3 mb-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary/10 text-secondary">
                      <Icon className="h-4 w-4" />
                    </span>
                    <h3 className="text-base font-semibold text-foreground leading-snug">
                      {p.title}
                    </h3>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {p.body}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Audience */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-6">
            {c.audienceTitle}
          </h2>
          <div className="rounded-2xl ring-1 ring-border/60 bg-card/70 backdrop-blur p-6 md:p-8">
            <ul className="space-y-4">
              {c.audience.map((a) => (
                <li key={a} className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-secondary flex-shrink-0" />
                  <span className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    {a}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Value / Benefits */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            {c.valueTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {c.values.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  className="group relative overflow-hidden rounded-2xl bg-card ring-1 ring-border/60 shadow-xs hover:shadow-md transition-all duration-300 p-6"
                >
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-secondary to-accent" />
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-secondary to-accent bg-clip-text text-transparent leading-none">
                      {v.metric}
                    </div>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary/10 text-secondary">
                      <Icon className="h-4 w-4" />
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2 leading-snug">
                    {v.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {v.body}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="relative container mx-auto px-4 max-w-3xl py-8">
          <div className="rounded-3xl bg-gradient-to-br from-secondary/15 via-card to-accent/10 ring-1 ring-secondary/30 p-6 md:p-8 shadow-[0_20px_60px_-24px_hsl(var(--secondary)/0.35)]">
            <h3 className="text-lg md:text-xl font-bold text-foreground mb-2">
              {c.ctaTitle}
            </h3>
            <p className="text-sm md:text-base text-muted-foreground mb-5">
              {c.ctaBody}
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://www.deep-insights.ai/book-demo"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-secondary to-accent px-6 py-3 text-sm font-semibold text-secondary-foreground shadow-lg shadow-secondary/30 transition-transform hover:-translate-y-0.5"
              >
                {c.cta1}
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                to={`${prefix}/compare/cargonova-vs-cargowise`}
                className="inline-flex items-center gap-2 rounded-full ring-1 ring-border/60 bg-card/70 backdrop-blur px-6 py-3 text-sm font-semibold text-foreground hover:ring-secondary/50 hover:text-secondary transition-colors"
              >
                {c.compareLabel}
              </Link>
              <Link
                to={`${prefix}/blog/what-is-ai-fms`}
                className="inline-flex items-center gap-2 rounded-full ring-1 ring-border/60 bg-card/70 backdrop-blur px-6 py-3 text-sm font-semibold text-foreground hover:ring-secondary/50 hover:text-secondary transition-colors"
              >
                {isZh
                  ? "什么是 AI FMS"
                  : isTh
                    ? "AI FMS คืออะไร"
                    : "What is an AI FMS?"}
              </Link>
              <Link
                to={`${prefix}/features`}
                className="inline-flex items-center gap-2 rounded-full ring-1 ring-border/60 bg-card/70 backdrop-blur px-6 py-3 text-sm font-semibold text-foreground hover:ring-secondary/50 hover:text-secondary transition-colors"
              >
                {isZh ? "平台功能" : isTh ? "ฟีเจอร์ของแพลตฟอร์ม" : "Platform features"}
              </Link>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default WhatIsCargoNova;
